import { LitElement, css, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";

/**
 * Generic popover anchored to a trigger element. Renders an
 * inline gear/cog trigger button; clicking opens a small panel
 * that positions itself absolutely below the trigger. Click
 * outside the popover (or press Escape) closes it.
 *
 * The panel slot is provided by the caller as light DOM children
 * — the popover delegates all interactions to the caller. We only
 * manage open/close state and the dismissal listener.
 */
@customElement("fleet-popover")
export class FleetPopover extends LitElement {
  @property({ type: Boolean }) open = false;
  @state() private docClickListener: ((e: MouseEvent) => void) | null = null;
  @state() private keydownListener: ((e: KeyboardEvent) => void) | null = null;

  static styles = css`
    :host {
      position: relative;
      display: inline-flex;
      align-items: center;
    }
    button.fleet-popover__trigger {
      all: unset;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 22px;
      height: 22px;
      border-radius: 4px;
      cursor: pointer;
      color: var(--fleet-muted, #8a96a8);
      transition: color 120ms ease-out, background 120ms ease-out;
    }
    button.fleet-popover__trigger:hover,
    button.fleet-popover__trigger[aria-expanded="true"] {
      color: var(--fleet-cyan, #00aaff);
      background: rgba(0, 170, 255, 0.1);
    }
    button.fleet-popover__trigger svg {
      width: 14px;
      height: 14px;
      stroke: currentColor;
      fill: none;
      stroke-width: 2;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
    .fleet-popover__panel {
      position: absolute;
      top: calc(100% + 6px);
      right: 0;
      /* JAISIU-3655 (followup 5) — the search-options popover for
       * the chat-thread / sidebar search holds a single toggle
       * row. Default min-width: 200px was wider than needed and
       * pushed the option label into a wide panel that read as
       * "modal-ish". Narrowing to 160 px keeps the toggle +
       * label comfortable without excess whitespace. Callers that
       * need more space can override .fleet-popover__panel with
       * their own min-width. */
      min-width: 160px;
      max-width: 220px;
      padding: 10px 12px;
      border: 1px solid rgba(0, 170, 255, 0.25);
      border-radius: 6px;
      background: rgba(8, 14, 22, 0.96);
      box-shadow: 0 6px 18px rgba(0, 0, 0, 0.4);
      font-size: 12px;
      color: var(--fleet-text, #d4dbe5);
      z-index: 100;
    }
    :root[data-theme="light"] .fleet-popover__panel {
      background: rgba(248, 250, 253, 0.98);
      border-color: rgba(0, 120, 200, 0.3);
      color: #1a2a3a;
    }
    .fleet-popover__panel[hidden] {
      display: none;
    }
  `;

  connectedCallback(): void {
    super.connectedCallback();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.detachListeners();
  }

  private attachListeners(): void {
    if (this.docClickListener) return;
    const onDocClick = (e: MouseEvent): void => {
      if (!this.open) return;
      const path = e.composedPath();
      if (!path.includes(this)) {
        this.open = false;
      }
    };
    const onKey = (e: KeyboardEvent): void => {
      if (this.open && e.key === "Escape") {
        this.open = false;
      }
    };
    document.addEventListener("click", onDocClick, true);
    document.addEventListener("keydown", onKey);
    this.docClickListener = onDocClick;
    this.keydownListener = onKey;
  }

  private detachListeners(): void {
    if (this.docClickListener) {
      document.removeEventListener("click", this.docClickListener, true);
      this.docClickListener = null;
    }
    if (this.keydownListener) {
      document.removeEventListener("keydown", this.keydownListener);
      this.keydownListener = null;
    }
  }

  updated(changed: Map<string, unknown>): void {
    if (changed.has("open")) {
      if (this.open) {
        this.attachListeners();
      } else {
        this.detachListeners();
      }
    }
  }

  private toggle(e: Event): void {
    e.stopPropagation();
    this.open = !this.open;
  }

  render() {
    return html`
      <button
        type="button"
        class="fleet-popover__trigger"
        aria-label="Search options"
        aria-expanded=${this.open ? "true" : "false"}
        title="Search options"
        @click=${this.toggle}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"
          />
          <circle cx="12" cy="12" r="3" />
        </svg>
      </button>
      <div class="fleet-popover__panel" ?hidden=${!this.open}>
        <slot></slot>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "fleet-popover": FleetPopover;
  }
}